import { trackOn, track, CLICK, DISPLAY } from '@updater-core/lib/tracker';
import { STATES, ITEM_KINDS, ITEM_DEFINITION_CODES } from '../../../constants';
import { getContent, buildAppState } from './dmv.data';
import { selectors, actions } from '../../../state';
import { trackEngagement } from '@mover-app/engagement';
import { createStore } from '@updater-core/lib/store';

export const BASE_PATH = '/dmv';
export function DmvConfig($stateProvider) {
  'ngInject';

  $stateProvider.state(STATES.STEP_DMV, {
    url: `${BASE_PATH}`,
    disableBreadcrumb: true,
    title: 'Update Your License',
    views: {
      '@': {
        template: '<div class="mount-point dmv-faq-container"></div>',
        controller: ($scope, updStore, $element) => {
          const states = getStates(updStore);
          updStore.dispatch(actions.step.isTerminalRoute());
          const trackOff = trackOn($element[0]);
          $scope.$on('$destroy', trackOff);
          trackEngagement({
            name: 'DMV',
            code: ITEM_DEFINITION_CODES.DMV,
          });

          const createInteractor = ({ initialContent, initialStates }) => {
            const appState = buildAppState(initialStates, initialContent);

            const { getState, setState, subscribe } = createStore({
              ...appState,
              showModal: !appState.fromState || !appState.toState,
            });

            if (getState().showModal) {
              track({
                domain: 'dmv',
                verb: DISPLAY,
                object: 'states_modal',
              });
            }

            const refetchContent = (states) =>
              getContent(states).then((content) => {
                const appState = buildAppState(states, content);
                setState({
                  ...appState,
                });
              });

            return {
              connect: (fn) => {
                fn(getState());
                return subscribe(fn);
              },
              actions: {
                updateStates: ({
                  fromState = getState().fromState,
                  toState = getState().toState,
                }) => {
                  refetchContent({ fromState, toState });
                  setState({ fromState, toState });
                },
                submit: () => {
                  const { fromState, toState } = getState();
                  track({
                    domain: 'dmv',
                    verb: CLICK,
                    object: 'get_started',
                    details: {
                      fromState,
                      toState,
                    },
                  });
                  setState({ showModal: false });
                },
                cancel: () => {
                  const { fromState, toState } = getState();
                  track({
                    domain: 'dmv',
                    verb: CLICK,
                    object: 'cancelled',
                    details: {
                      fromState,
                      toState,
                    },
                  });
                  updStore.dispatch(actions.router.stateGo(STATES.DASHBOARD));
                },
              },
            };
          };

          Promise.all([import('./dmv.app.js'), getContent(states)]).then(
            ([{ renderApp, destroyApp }, content]) => {
              let destroy;
              const interactor = createInteractor({
                initialStates: states,
                initialContent: content,
              });
              interactor.connect((state) => {
                const props = {
                  ...state,
                  ...interactor.actions,
                };
                destroy = buildApp($element, renderApp, props, destroyApp);
              });
              $scope.$on('$destroy', () => destroy?.());
            }
          );
        },
      },
    },
    scrollTop: true,
    data: {
      permissions: 'signedIn',
      moveItem: {
        type: ITEM_KINDS.DMV,
        singleton: true,
      },
    },
  });
}

function buildApp($element, renderApp, state, destroyApp) {
  const div = $element[0].querySelector('.mount-point');
  renderApp(div, state);
  return () => destroyApp(div);
}

function getStates(updStore) {
  const state = updStore.getState();
  const { current } = selectors;

  const move = current.move(state);
  const fromState = move.fromAddress(state)?.state;
  const toState = move.toAddress(state)?.state;

  return {
    fromState,
    toState,
  };
}
